import type { ReadConfig } from 'api-read-hook';
import type { HydraCollection } from '../../types';

import { useApiRead } from 'api-read-hook';

import * as MediaUpload from './MediaUpload';
import * as Program from './Program';
import { makeQueryString } from '../../util';

export type V_Detail = {
  '@id': string;
  '@type': 'ProgramWeek';
  id: string;
  number: number;
  dailyTips: Array<{
    '@id': string;
    '@type': 'DailyTip';
    id: string;
    day: number;
    title: string;
    description: string;
    isSaved: boolean | null;
    isViewed: boolean | null;
  }>;
  tasks: Array<{
    '@id': string;
    '@type': 'WeeklyTask';
    id: string;
    title: string;
    resourceTitle: string;
    position: number;
    description: string;
    image: MediaUpload.BaseView | null;
    completedDates: Array<string> | null;
    isSaved: boolean | null;
    isViewed: boolean | null;
  }>;
  prompts: Array<{
    '@id': string;
    '@type': 'PsychologyPrompt';
    id: string;
    title: string;
    position: number;
    userAnswer: {
      '@id': string;
      '@type': 'UserSimpleAnswer';
      answer: string;
    } | null;
  }>;
  reflectionPoints: Array<{
    '@id': string;
    '@type': 'ReflectionPoint';
    id: string;
    title: string;
    position: number;
    userAnswer: {
      '@id': string;
      '@type': 'UserSimpleAnswer';
      answer: string;
    } | null;
  }>;
  lessons: Array<{
    '@id': string;
    '@type': 'Lesson';
    id: string;
    title: string;
    position: number;
    thumb: MediaUpload.BaseView;
    video: MediaUpload.BaseView | null;
    userAnswer: {
      '@id': string;
      '@type': 'UserSimpleAnswer';
      answer: string;
    } | null;
    myEngagement: {
      '@id': string;
      '@type': 'UserLessonEngagement';
      completedAt: string | null;
    } | null;
    isCompleted: boolean | null;
    isSaved: boolean | null;
    slides: Array<{
      '@id': string;
      '@type': 'LessonSlide';
      templateName: string;
      templateData: object;
    }>;
  }>;
  isCompleted: boolean | null;
  /** The member's own dates for this week, including any pause or frozen stretch in it */
  startDate: string | null;
  endDate: string | null;
};

export type V_ListAsAdmin = {
  '@id': string;
  '@type': 'ProgramWeek';
  id: string;
  number: number;
  program: Program.V;
  publishedTips: number;
  publishedTasks: number;
  publishedPrompts: number;
  publishedPoints: number;
  publishedLessons: number;
};

/** Weeks of every program, or of one with `program` (its IRI, e.g. /programs/{id}). */
export function useReadCollectionAdminList(
  queryParams?: { program?: string },
  options?: ReadConfig,
) {
  return useApiRead<HydraCollection<V_ListAsAdmin>>(
    `/program-weeks/admin-list${makeQueryString(queryParams)}`,
    options,
  );
}

export function useReadItemProgramWeeksbyNumberWeek(
  id: string,
  week: string,
  options?: ReadConfig,
) {
  return useApiRead<V_Detail>(
    `/users/${id}/program-weeks/by-number/${week}`,
    options,
  );
}

/** One week of one program, with the member's activity. */
export function useReadItemByProgram(
  userId: string,
  programId: string,
  week: string,
  options?: ReadConfig,
) {
  return useApiRead<V_Detail>(
    `/users/${userId}/programs/${programId}/weeks/by-number/${week}`,
    options,
  );
}
